/**
 * Governança > TI > Monitoramento
 * Fase 1 Observabilidade — Application Insights + OpenTelemetry (App Service)
 *
 * Tabs:
 *   Overview | Serviços | Healthchecks | Alertas | Incidentes | Observabilidade | Azure
 *
 * NÃO usa mock data. Todos os estados vazios são tratados explicitamente.
 * Auth: dentro do GovernanceGuard via GovernanceLayout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Server, CheckCircle, XCircle, AlertCircle,
  Clock, RefreshCw, ExternalLink, Database, Radio,
  Cpu, Zap, Monitor, BarChart2, ShieldAlert, Cloud,
  ChevronRight, Info, Wifi, WifiOff
} from 'lucide-react';
import {
  getMonitoringOverview,
  getMonitoringServices,
  getMonitoringHealthchecks,
  getMonitoringObservability,
} from '../../services/governance';
import { getOpsAlerts, getGovernanceIncidents } from '../../services/governance';
import { COLORS } from '../../components/governance/governanceTheme';

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const map = {
    healthy:        { bg: '#dcfce7', color: '#15803d', label: 'Healthy' },
    ok:             { bg: '#dcfce7', color: '#15803d', label: 'OK' },
    configured:     { bg: '#dbeafe', color: '#1d4ed8', label: 'Configured' },
    degraded:       { bg: '#fef9c3', color: '#a16207', label: 'Degraded' },
    critical:       { bg: '#fee2e2', color: '#b91c1c', label: 'Critical' },
    unhealthy:      { bg: '#fee2e2', color: '#b91c1c', label: 'Unhealthy' },
    unknown:        { bg: '#f3f4f6', color: '#4b5563', label: 'Unknown' },
    missing:        { bg: '#fef3c7', color: '#92400e', label: 'Missing' },
    missing_config: { bg: '#fef3c7', color: '#92400e', label: 'No Config' },
    not_deployed:   { bg: '#f3f4f6', color: '#6b7280', label: 'Not Deployed' },
  };
  const s = map[status] || map.unknown;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

const SeverityBadge = ({ severity }) => {
  const map = {
    critical: { bg: '#fee2e2', color: '#b91c1c' },
    high:     { bg: '#ffedd5', color: '#c2410c' },
    medium:   { bg: '#fef9c3', color: '#a16207' },
    low:      { bg: '#dbeafe', color: '#1d4ed8' },
    info:     { bg: '#f0fdf4', color: '#15803d' },
  };
  const key = (severity || '').toLowerCase();
  const s = map[key] || { bg: '#f3f4f6', color: '#4b5563' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {severity || '—'}
    </span>
  );
};

const MetricCard = ({ icon: Icon, title, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
    <div
      className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
      style={{ backgroundColor: `${color}18` }}
    >
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div className="min-w-0">
      <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-0.5">{title}</div>
      <div className="text-2xl font-extrabold text-gray-900 leading-tight">{value ?? '—'}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, message }) => (
  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
    <Icon className="w-8 h-8 mb-3 opacity-40" />
    <p className="text-sm">{message}</p>
  </div>
);

const LoadingSpinner = ({ text = 'Carregando...' }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3">
    <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-700 rounded-full animate-spin" />
    <p className="text-sm text-gray-500">{text}</p>
  </div>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="bg-rose-50 border border-rose-200 rounded-xl p-5">
    <div className="flex items-center gap-3 text-rose-700 mb-1">
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      <span className="font-bold text-sm">Erro ao carregar</span>
    </div>
    <p className="text-rose-600 text-sm">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 hover:text-rose-900 underline"
      >
        <RefreshCw className="w-3 h-3" /> Tentar novamente
      </button>
    )}
  </div>
);

const TableSection = ({ headers, rows, emptyMsg }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-100">
          {headers.map((h) => (
            <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={headers.length} className="px-4 py-10 text-center text-sm text-gray-400">
              {emptyMsg}
            </td>
          </tr>
        ) : (
          rows
        )}
      </tbody>
    </table>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Tab panels
// ─────────────────────────────────────────────────────────────

const OverviewTab = ({ data, onRefresh, refreshing }) => {
  if (!data) return <LoadingSpinner />;

  const { status, services = [], workers = [], alerts = {}, incidents = {}, observability = {}, timestamp } = data;

  const statusColors = {
    healthy: COLORS.success,
    degraded: COLORS.warning,
    critical: COLORS.error,
    unknown: COLORS.textMuted,
  };
  const overallColor = statusColors[status] || statusColors.unknown;

  return (
    <div className="space-y-6">
      {/* Platform status banner */}
      <div
        className="rounded-xl p-5 flex items-center justify-between"
        style={{ backgroundColor: `${overallColor}10`, border: `1.5px solid ${overallColor}30` }}
      >
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6" style={{ color: overallColor }} />
          <div>
            <div className="text-lg font-extrabold" style={{ color: overallColor }}>
              Platform {(status || 'unknown').charAt(0).toUpperCase() + (status || 'unknown').slice(1)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              Última verificação: {timestamp ? new Date(timestamp).toLocaleString('pt-BR') : '—'}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-800 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={AlertCircle}
          title="Alertas Ativos"
          value={alerts.active_count ?? 0}
          sub={`${alerts.critical_count ?? 0} críticos`}
          color={COLORS.warning}
        />
        <MetricCard
          icon={ShieldAlert}
          title="Incidentes Abertos"
          value={incidents.open_count ?? 0}
          sub="na plataforma"
          color={COLORS.error}
        />
        <MetricCard
          icon={Server}
          title="Serviços"
          value={services.length}
          sub="monitorados"
          color={COLORS.info}
        />
        <MetricCard
          icon={Cpu}
          title="Workers"
          value={workers.length}
          sub="definidos"
          color={COLORS.primary}
        />
      </div>

      {/* Services grid */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Serviços</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.length === 0 ? (
            <div className="col-span-3 bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">
              Nenhum serviço reportado ainda
            </div>
          ) : (
            services.map((svc) => (
              <div key={svc.key} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3">
                <div className="mt-0.5">
                  {svc.status === 'healthy' || svc.status === 'ok'
                    ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                    : svc.status === 'unhealthy' || svc.status === 'critical'
                    ? <XCircle className="w-4 h-4 text-red-500" />
                    : <Info className="w-4 h-4 text-gray-400" />
                  }
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-gray-800">{svc.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusBadge status={svc.status} />
                    {svc.response_ms != null && (
                      <span className="text-xs text-gray-400">{svc.response_ms} ms</span>
                    )}
                  </div>
                  {svc.last_checked_at && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {new Date(svc.last_checked_at).toLocaleTimeString('pt-BR')}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Workers */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Workers</h3>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {workers.length === 0 ? (
            <EmptyState icon={Cpu} message="Nenhum worker registrado" />
          ) : (
            workers.map((w, i) => (
              <div
                key={w.key}
                className={`px-5 py-4 flex items-start gap-4 ${i > 0 ? 'border-t border-gray-100' : ''}`}
              >
                <Cpu className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-800">{w.name}</span>
                    <StatusBadge status={w.status} />
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{w.app_service}</div>
                  {w.note && <div className="text-xs text-gray-400 mt-0.5 italic">{w.note}</div>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Observability quick glance */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Stack de Observabilidade</h3>
        <div className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Application Insights', value: observability.application_insights, icon: BarChart2 },
            { label: 'Log Analytics', value: observability.log_analytics, icon: Monitor },
            { label: 'Sampling Ratio', value: observability.sampling_ratio != null ? `${Math.round(observability.sampling_ratio * 100)}%` : '—', icon: Radio },
            { label: 'Prometheus / Grafana', value: 'N/A (App Service)', icon: Cloud },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="text-center">
              <div className="flex justify-center mb-1">
                <Icon className="w-5 h-5 text-gray-400" />
              </div>
              <div className="text-xs text-gray-500 mb-1">{label}</div>
              <StatusBadge status={value === 'configured' || value === 'N/A (App Service)' ? 'configured' : value || 'unknown'} />
            </div>
          ))}
        </div>
        {observability.note && (
          <p className="mt-2 text-xs text-gray-400 italic flex items-start gap-1.5">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
            {observability.note}
          </p>
        )}
      </div>
    </div>
  );
};

const ServicesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getMonitoringServices();
      setData(resp);
    } catch (e) {
      setError(e.message || 'Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;

  const services = data?.services || data || [];

  return (
    <div className="space-y-4">
      {services.length === 0 ? (
        <EmptyState icon={Server} message="Nenhum serviço com dados ainda. Execute um health check no backend para popular ops_health_snapshots." />
      ) : (
        services.map((svc) => (
          <div key={svc.key} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-base font-bold text-gray-900">{svc.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{svc.resource_type || svc.source || '—'}</div>
              </div>
              <div className="flex items-center gap-3">
                {svc.response_ms != null && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {svc.response_ms} ms
                  </span>
                )}
                <StatusBadge status={svc.status} />
              </div>
            </div>
            {svc.last_checked_at && (
              <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Última verificação: {new Date(svc.last_checked_at).toLocaleString('pt-BR')}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

const HealthchecksTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resource, setResource] = useState('');
  const [hours, setHours] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { hours };
      if (resource) params.resource = resource;
      const resp = await getMonitoringHealthchecks(params);
      setData(resp);
    } catch (e) {
      setError(e.message || 'Erro ao carregar healthchecks');
    } finally {
      setLoading(false);
    }
  }, [resource, hours]);

  useEffect(() => { load(); }, [load]);

  const snapshots = data?.snapshots || data || [];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Recurso</label>
          <input
            type="text"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
            placeholder="Todos"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Últimas horas</label>
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {[1, 6, 12, 24, 48, 168].map((h) => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: COLORS.primary }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Filtrar
        </button>
      </div>

      {loading ? <LoadingSpinner /> : error ? <ErrorBanner message={error} onRetry={load} /> : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <TableSection
            headers={['Recurso', 'Tipo', 'Status', 'Response (ms)', 'Registrado em']}
            emptyMsg="Nenhum snapshot encontrado. A tabela ops_health_snapshots será populada após a aplicação da migration 277."
            rows={snapshots.map((s, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{s.resource_name || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.resource_type || '—'}</td>
                <td className="px-4 py-3"><StatusBadge status={s.health_status} /></td>
                <td className="px-4 py-3 text-sm text-gray-600">{s.response_time_ms != null ? `${s.response_time_ms} ms` : '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {s.captured_at ? new Date(s.captured_at).toLocaleString('pt-BR') : '—'}
                </td>
              </tr>
            ))}
          />
        </div>
      )}
    </div>
  );
};

const AlertasTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getOpsAlerts();
      setData(resp);
    } catch (e) {
      setError(e.message || 'Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const alerts = data?.alerts || data?.items || (Array.isArray(data) ? data : []);

  return (
    <div>
      {loading ? <LoadingSpinner /> : error ? <ErrorBanner message={error} onRetry={load} /> : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <TableSection
            headers={['Severidade', 'Alerta', 'Recurso', 'Ativado em']}
            emptyMsg="Nenhum alerta ativo. Bom sinal 🎉"
            rows={alerts.map((a, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3"><SeverityBadge severity={a.severity || a.alert_severity} /></td>
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{a.alert_name || a.name || a.title || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{a.resource_name || a.source || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {(a.fired_at || a.created_at) ? new Date(a.fired_at || a.created_at).toLocaleString('pt-BR') : '—'}
                </td>
              </tr>
            ))}
          />
        </div>
      )}
    </div>
  );
};

const IncidentesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getGovernanceIncidents({ limit: 30 }, 'production');
      setData(resp);
    } catch (e) {
      setError(e.message || 'Erro ao carregar incidentes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const incidents = data?.incidents || (Array.isArray(data) ? data : []);

  return (
    <div>
      {loading ? <LoadingSpinner /> : error ? <ErrorBanner message={error} onRetry={load} /> : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <TableSection
            headers={['Código', 'Título', 'Severidade', 'Status', 'Aberto em']}
            emptyMsg="Nenhum incidente registrado."
            rows={incidents.map((inc, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 text-xs font-mono text-gray-500">{inc.incident_code || `#${inc.incident_id || i+1}`}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{inc.title || '—'}</td>
                <td className="px-4 py-3"><SeverityBadge severity={inc.severity} /></td>
                <td className="px-4 py-3"><StatusBadge status={inc.status} /></td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {inc.opened_at ? new Date(inc.opened_at).toLocaleString('pt-BR') : '—'}
                </td>
              </tr>
            ))}
          />
        </div>
      )}
    </div>
  );
};

const ObservabilidadeTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getMonitoringObservability();
      setData(resp);
    } catch (e) {
      setError(e.message || 'Erro ao carregar estado de observabilidade');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;

  const obs = data?.observability || data || {};

  const items = [
    {
      key: 'application_insights',
      label: 'Application Insights',
      description: 'APM principal — traces, exceptions, performance',
      value: obs.application_insights,
      icon: BarChart2,
    },
    {
      key: 'log_analytics',
      label: 'Log Analytics Workspace',
      description: 'Centralização de logs — queries KQL',
      value: obs.log_analytics,
      icon: Monitor,
    },
    {
      key: 'opentelemetry',
      label: 'OpenTelemetry (azure-monitor-opentelemetry)',
      description: `SDK: azure-monitor-opentelemetry ≥ 1.6.4 | Sampling: ${obs.sampling_ratio != null ? Math.round(obs.sampling_ratio * 100) + '%' : '—'}`,
      value: obs.application_insights === 'configured' ? 'configured' : 'depends_on_appinsights',
      icon: Zap,
    },
    {
      key: 'prometheus',
      label: 'Prometheus',
      description: obs.note || 'Fora de escopo para App Service. Avaliar para AKS/VMs + Azure Arc.',
      value: obs.prometheus || 'not_applicable_app_service_architecture',
      icon: Radio,
    },
    {
      key: 'grafana',
      label: 'Managed Grafana',
      description: 'Fora de escopo para App Service. Avaliar no futuro.',
      value: obs.grafana || 'not_applicable_app_service_architecture',
      icon: BarChart2,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          A stack de Fase 1 usa <strong>Application Insights + OpenTelemetry</strong> nativo do Azure App Service.
          AMW, Prometheus e Managed Grafana estão fora de escopo para a arquitetura atual e serão reavaliados para AKS/VMs.
        </p>
      </div>

      {items.map(({ key, label, description, value, icon: Icon }) => {
        const isOk = value === 'configured' || value === 'not_applicable_app_service_architecture';
        return (
          <div key={key} className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
              style={{ backgroundColor: isOk ? '#d1fae5' : '#fee2e2' }}
            >
              <Icon className="w-5 h-5" style={{ color: isOk ? '#059669' : '#dc2626' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <span className="text-sm font-bold text-gray-800">{label}</span>
                <StatusBadge status={value || 'unknown'} />
              </div>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
        );
      })}

      {/* Workers OTel */}
      {obs.workers && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Workers Instrumentados</h3>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {obs.workers.map((w, i) => (
              <div
                key={w.name || i}
                className={`px-5 py-4 flex items-center justify-between ${i > 0 ? 'border-t border-gray-100' : ''}`}
              >
                <div>
                  <div className="text-sm font-bold text-gray-800">{w.name}</div>
                  {w.note && <div className="text-xs text-gray-400">{w.note}</div>}
                </div>
                <StatusBadge status={w.otel_status || 'configured'} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AzureTab = () => {
  const azureLinks = [
    {
      section: 'Compute',
      items: [
        {
          label: 'API App Service — api-arenastats',
          url: 'https://portal.azure.com/#view/WebsitesExtension/WebsiteMenuBlade/~/Overview/resourceId/%2Fsubscriptions%2F{sub}%2FresourceGroups%2Farena_stats%2Fproviders%2FMicrosoft.Web%2Fsites%2Fapi-arenastats',
          icon: Server,
          note: 'Portal → App Service → api-arenastats',
        },
        {
          label: 'Worker App Service — arenastats-worker',
          url: 'https://portal.azure.com/#view/WebsitesExtension/WebsiteMenuBlade/~/Overview/resourceId/%2Fsubscriptions%2F{sub}%2FresourceGroups%2Farena_stats%2Fproviders%2FMicrosoft.Web%2Fsites%2Farenastats-worker',
          icon: Cpu,
          note: 'Portal → App Service → arenastats-worker',
        },
      ],
    },
    {
      section: 'Observabilidade',
      items: [
        {
          label: 'Application Insights',
          url: 'https://portal.azure.com/#browse/microsoft.insights%2Fcomponents',
          icon: BarChart2,
          note: 'Traces, exceptions, performance metrics',
        },
        {
          label: 'Log Analytics Workspace',
          url: 'https://portal.azure.com/#browse/microsoft.operationalinsights%2Fworkspaces',
          icon: Monitor,
          note: 'Queries KQL centralizadas',
        },
      ],
    },
    {
      section: 'Mensageria & Dados',
      items: [
        {
          label: 'Azure Service Bus',
          url: 'https://portal.azure.com/#browse/Microsoft.ServiceBus%2Fnamespaces',
          icon: Radio,
          note: 'Queue: ingestion-queue',
        },
        {
          label: 'Azure SQL Database',
          url: 'https://portal.azure.com/#browse/Microsoft.Sql%2Fservers',
          icon: Database,
          note: 'arenastats-db (Serverless tier)',
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          Links diretos ao Azure Portal. Substitua <code className="bg-amber-100 px-1 rounded">{'{sub}'}</code> pelo Subscription ID correto nos links abaixo, ou navegue manualmente pelo portal.
        </p>
      </div>

      {azureLinks.map(({ section, items }) => (
        <div key={section}>
          <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{section}</h3>
          <div className="space-y-2">
            {items.map(({ label, url, icon: Icon, note }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition-all duration-200 group"
              >
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: '#e0f2fe' }}
                >
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                    {label}
                  </div>
                  {note && <div className="text-xs text-gray-400 mt-0.5">{note}</div>}
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',        label: 'Overview',        icon: Activity },
  { id: 'services',        label: 'Serviços',         icon: Server },
  { id: 'healthchecks',    label: 'Healthchecks',     icon: CheckCircle },
  { id: 'alerts',          label: 'Alertas',          icon: AlertCircle },
  { id: 'incidents',       label: 'Incidentes',       icon: ShieldAlert },
  { id: 'observability',   label: 'Observabilidade',  icon: Zap },
  { id: 'azure',           label: 'Azure',            icon: Cloud },
];

const GovernanceTIMonitoringPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setOverviewLoading(true);
    setOverviewError(null);
    try {
      const resp = await getMonitoringOverview();
      setOverviewData(resp?.data || resp);
    } catch (e) {
      setOverviewError(e.message || 'Erro ao carregar overview');
    } finally {
      setOverviewLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    loadOverview(true);
  };

  // OTel connection indicator (top-right badge)
  const obsState = overviewData?.observability;
  const aiConnected = obsState?.application_insights === 'configured';

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Activity className="w-7 h-7" style={{ color: COLORS.primary }} />
            <h1 className="text-2xl font-extrabold text-gray-900">Monitoramento</h1>
            {!overviewLoading && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                style={{
                  backgroundColor: aiConnected ? '#dcfce7' : '#fef3c7',
                  color: aiConnected ? '#15803d' : '#92400e',
                }}
              >
                {aiConnected
                  ? <><Wifi className="w-3 h-3" /> Application Insights</>
                  : <><WifiOff className="w-3 h-3" /> AI não configurado</>
                }
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Fase 1 — Application Insights + OpenTelemetry · App Service Architecture
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || overviewLoading}
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 hover:text-gray-900 hover:shadow-sm transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b border-gray-200">
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-bold whitespace-nowrap transition-all border-b-2 ${
                  active ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            overviewLoading
              ? <LoadingSpinner />
              : overviewError
              ? <ErrorBanner message={overviewError} onRetry={() => loadOverview()} />
              : <OverviewTab data={overviewData} onRefresh={handleRefresh} refreshing={refreshing} />
          )}
          {activeTab === 'services'      && <ServicesTab />}
          {activeTab === 'healthchecks'  && <HealthchecksTab />}
          {activeTab === 'alerts'        && <AlertasTab />}
          {activeTab === 'incidents'     && <IncidentesTab />}
          {activeTab === 'observability' && <ObservabilidadeTab />}
          {activeTab === 'azure'         && <AzureTab />}
        </div>
      </div>
    </div>
  );
};

export default GovernanceTIMonitoringPage;
